import { useResponsive } from '../../../hooks/useResponsive';
import { ErrorDisplay } from '../../ErrorDisplay';
import { DataTable } from '../DataTable';
import { MobileList } from '../MobileList';
import { MobileSelectionBar } from '../MobileList/MobileSelectionBar';
import { ResponsiveDataViewProps } from './types';

/**
 * ResponsiveDataView - Automatically switches between desktop table and mobile list
 * based on screen size
 */
export function ResponsiveDataView<TData>({
  data,
  isLoading = false,
  onRefresh,
  error,
  getRowId,
  columns,
  getSubRows,
  expanded,
  onExpandedChange,
  renderListItem,
  enableSelection = false,
  selectedIds = [],
  onSelectionChange,
  onRowClick,
  pageIndex,
  pageSize,
  totalPages,
  totalCount,
  onPageChange,
  onPageSizeChange,
  columnFilters,
  toolbarActions,
  activeFilters,
  enableManageColumns,
  multiSelectActions,
  mobileSelectionActions,
  onClearSelection,
  emptyState,
  emptyStateMessage,
  sorting,
  onSortingChange,
  columnPinning,
  onColumnPinningChange,
  columnVisibility,
  onColumnVisibilityChange,
  defaultColumnVisibility,
  columnOrder,
  onColumnOrderChange,
  columnSizing,
  onColumnSizingChange,
  resetColumnSizing,
  breakpoint = 768,
  forceViewMode,
  ariaLabel,
  title,
  icon,
}: ResponsiveDataViewProps<TData>) {
  const { prefersMobileLayout } = useResponsive(breakpoint);

  // Determine which view to show
  const showMobile =
    forceViewMode === 'mobile' ||
    (forceViewMode !== 'desktop' && prefersMobileLayout);

  // Default getRowId if not provided
  const getItemId =
    getRowId ||
    ((item: TData) => {
      if (item && typeof item === 'object' && 'id' in item) {
        return String(item.id);
      }
      throw new Error(
        'ResponsiveDataView: getRowId is required when data items do not have an "id" property'
      );
    });

  if (showMobile) {
    const hasSelection = enableSelection && selectedIds.length > 0;

    // Mobile List View
    return (
      <div className='flex h-full max-w-full flex-1 flex-col overflow-hidden'>
        {/* Mobile List with bottom padding when selection bar is visible */}
        <div
          className='max-w-full flex-1 overflow-auto'
          style={{ paddingBottom: hasSelection ? '60px' : undefined }}
        >
          <MobileList
            data={data}
            isLoading={isLoading}
            onRefresh={onRefresh}
            getRowId={getItemId}
            renderItem={(item, config) => renderListItem(item, config)}
            enableSelection={enableSelection}
            selectedIds={selectedIds}
            onSelectionChange={onSelectionChange}
            onItemClick={onRowClick}
            title={title}
            icon={icon}
            headerActions={toolbarActions}
            currentPage={(pageIndex ?? 0) + 1}
            totalPages={totalPages}
            totalCount={totalCount}
            rowsPerPage={pageSize}
            onPageChange={(page) => onPageChange?.(page - 1)}
            emptyState={
              error ? (
                <ErrorDisplay error={error} />
              ) : emptyState ? (
                emptyState
              ) : (
                <div className='text-center text-gray-500'>
                  <p className='mb-1 text-lg font-medium'>No items found</p>
                  {emptyStateMessage && (
                    <p className='text-sm'>{emptyStateMessage}</p>
                  )}
                </div>
              )
            }
            ariaLabel={ariaLabel}
          />
        </div>

        {/* Fixed mobile selection bar at bottom */}
        {mobileSelectionActions && (
          <MobileSelectionBar
            selectedCount={selectedIds.length}
            actions={mobileSelectionActions}
            onClearSelection={onClearSelection}
          />
        )}
      </div>
    );
  }

  // Desktop Table View
  return (
    <div className='flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden'>
      {/* DataTable */}
      <DataTable
        toolbarActions={
          enableSelection && selectedIds.length > 0 && multiSelectActions
            ? multiSelectActions
            : toolbarActions
        }
        data={data}
        columns={columns}
        isLoading={isLoading}
        error={error}
        getRowId={getItemId}
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sorting={sorting}
        onSortingChange={onSortingChange}
        columnPinning={columnPinning}
        onColumnPinningChange={onColumnPinningChange}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={onColumnVisibilityChange}
        defaultColumnVisibility={defaultColumnVisibility}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        getSubRows={getSubRows}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        resetColumnSizing={resetColumnSizing}
        columnFilters={columnFilters}
        activeFilters={activeFilters}
        enableManageColumns={enableManageColumns}
        onRowClick={onRowClick}
        selectedIds={enableSelection ? selectedIds : undefined}
        emptyStateMessage={emptyStateMessage || 'No data found'}
        emptyStateDescription='Try adjusting your filters or search criteria'
        title={title}
        ariaLabel={ariaLabel}
      />
    </div>
  );
}
